import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { alert } from 'devextreme/ui/dialog';
import { Product } from '../../../../Model/Catalog/Product';
import { Catalog } from '../../../../Model/Catalog/Catalog';
import { ProductSupplier } from '../../../../Model/Catalog/ProductSupplier';
import { PurchasingUnit } from '../../../../Model/Catalog/PurchasingUnit';
import { ModelService } from '../../../../Services/model.service';
import { TranslateService } from '@ngx-translate/core';
import { UserManagementService } from '../../../../Services/userManagment.service';
import { LoginService } from '../../../../Services/login.service';
import { WawiListService } from '../../../../Services/wawiList.service';
import { WawiListElement } from '../../../../Model/wawi/WawiListElement';
import { CustomItemCreatingEvent } from 'devextreme/ui/select_box';

interface PurchasingUnitRow {
  supplierId: string;
  supplierName: string;
  supplierOrder: string;
  unit: string;
  pid: string;
  internationalId: string;
  gevisMapping: string;
  contentQuantity: number;
  depth: number;
  length: number;
  width: number;
  weight: number;
  quantityMin: number;
  quantityMax: number;
  backing: PurchasingUnit[];
}

type UnitItem = { value: string; displayValue: string };

@Component({
  selector: 'purchasingUnits',
  templateUrl: 'purchasingUnits.component.html',
  styleUrls: ['./purchasingUnits.component.css'],
  standalone: false
})
export class PurchasingUnitsComponent implements OnInit, OnChanges {
  @Input() product: Product = null;
  @Input() customerId: string = '';
  @Input() templateView: boolean = false;
  @Input() showLinkButtons: boolean = false;
  @Input() exportMode: boolean = false;
  @Input() selectedElements: string[] = [''];
  @Input() showDragHandle: boolean = false;
  @Input() dragFilter: string = 'noDrag';
  @Input() units: UnitItem[] = [];

  @Output() onValueChanged = new EventEmitter<any>();
  @Output() elementSelected = new EventEmitter<string>();
  @Output() excelElementDropped = new EventEmitter<any>();
  @Output() elementDeleted = new EventEmitter<any>();

  rows: PurchasingUnitRow[] = [];
  supplierNames: { id: string; name: string }[] = [];
  gevisMappingList: WawiListElement[] = [];
  baseUnitItems: UnitItem[] = [];
  hasActiveState = false;
  private grid: any;

  constructor(
    public modelService: ModelService,
    public translate: TranslateService,
    public userManagement: UserManagementService,
    public loginService: LoginService,
    public wawiListService: WawiListService,
    private cdr: ChangeDetectorRef
  ) {
    this.isGevis = this.isGevis.bind(this);
    this.applyFilterReset = this.applyFilterReset.bind(this);
  }

  templateSelect(identifier: string, system: string) {
    this.elementSelected.emit(`${identifier}_${system}`);
  }

  excelElementDrop(e) {
    this.excelElementDropped.emit(e);
  }

  update(event: any, row: PurchasingUnitRow, field: string, system: string) {
    if (row) {
      this.applyRowToBacking(row);
    }
    if (event) {
      event.dField = field;
      event.dSystem = system;
      event.dElement = '-1';
      event.createTemplateItem = true;
      event.supplierPackingChanged = true;
    }
    this.onValueChanged.emit(event);
  }

  readOnlyForLinkedFields(): boolean {
    return false;
  }

  showClearButton(): boolean {
    return !this.templateView;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.units && this.product) {
      this.buildBaseUnitItems();
    }
  }

  ngOnInit(): void {
    lastValueFrom(this.wawiListService.getListByName('EinheitenCodes', this.customerId || this.loginService.currentCustomer.id))
      .then((list) => {
        this.gevisMappingList = list?.fields ?? [];
        this.refresh();
      })
      .catch(() => {
        this.gevisMappingList = [];
      });

    this.refresh();
  }

  refresh(): void {
    this.syncPurchasingUnits();
    this.ensureBaseUnit();
    this.buildBaseUnitItems();
    this.generateSupplierNames();
    this.buildRows();
    this.cdr.detectChanges();
  }

  isGevis(): boolean {
    return this.loginService.hasAddonGevis;
  }

  getGevisMappedValue(code: string): string {
    if (this.gevisMappingList == null) {
      return '';
    }
    const match = this.gevisMappingList.find((x) => x.key === code);
    return match?.value ?? '';
  }

  onGridInitialized(e) {
    this.grid = e.component;
  }

  onContentReady(e) {
    this.recomputeActiveState(e.component);
  }

  private recomputeActiveState(component: any) {
    const state = component.state();
    this.hasActiveState = this.computeHasActiveState(state);
    this.cdr.detectChanges();
  }

  private computeHasActiveState(state: any): boolean {
    if (!state) {
      return false;
    }
    if (state.filterValue && state.filterValue.length) {
      return true;
    }
    if (state.searchPanel?.text) {
      return true;
    }

    const cols = state.columns ?? [];
    for (const c of cols) {
      if (!c) {
        continue;
      }
      if (c.filterValues?.length) {
        return true;
      }
      if (c.filterValue !== undefined && c.filterValue !== null && c.filterValue !== '') {
        return true;
      }
      if (typeof c.groupIndex === 'number' && c.groupIndex >= 0) {
        return true;
      }
      if (typeof c.sortIndex === 'number' && c.sortIndex >= 0) {
        return true;
      }
    }

    return false;
  }

  applyFilterReset() {
    const grid = this.grid;
    if (grid == null) {
      return;
    }
    grid.clearGrouping();
    grid.clearFilter();
    grid.clearSorting();
    grid.option('searchPanel.text', '');
    this.hasActiveState = false;
    this.cdr.detectChanges();
  }

  private generateSupplierNames() {
    const map = new Map<string, string>();
    for (const supplier of this.product.suppliers) {
      const id = supplier.supplierId?.trim();
      if (id && !map.has(id)) {
        map.set(id, supplier.supplierName?.trim() || id);
      }
    }
    this.supplierNames = [...map.entries()].map(([id, name]) => ({ id, name }));
  }

  private ensureBaseUnit() {
    if (this.product.purchasingBaseUnit?.trim()) {
      return;
    }
    const orderUnit = this.product.orderDetail?.orderUnit?.trim();
    if (orderUnit) {
      this.product.purchasingBaseUnit = orderUnit;
    }
  }

  private buildBaseUnitItems() {
    const baseUnit = this.getBaseUnit()?.trim();
    const items = [...this.units];
    if (baseUnit && items.every((x) => x.value !== baseUnit)) {
      items.push({ value: baseUnit, displayValue: baseUnit });
    }
    this.baseUnitItems = items;
  }

  onBaseUnitCustomItemCreating(e: CustomItemCreatingEvent) {
    const existingItem = this.baseUnitItems.find((x) => x.value === e.text);
    if (existingItem) {
      e.customItem = existingItem;
      return;
    }
    const customItem = { value: e.text, displayValue: e.text };
    this.baseUnitItems = [...this.baseUnitItems, customItem];
    e.customItem = customItem;
  }

  private syncPurchasingUnits() {
    for (const supplier of this.product.suppliers) {
      if (supplier.purchasingUnits == null) {
        supplier.purchasingUnits = [];
      }
      const units = [supplier.priceAllocationUnitCode, supplier.originalPriceAllocationUnitCode]
        .map((x) => x?.trim())
        .filter((x) => !!x)
        .filter((x, i, a) => a.indexOf(x) === i);

      supplier.purchasingUnits = supplier.purchasingUnits.filter((x) => units.includes(x.unit));

      let order = supplier.purchasingUnits.length;
      for (const unit of units) {
        let pu = supplier.purchasingUnits.find((x) => x.unit === unit);
        if (pu == null) {
          pu = new PurchasingUnit();
          pu.order = ++order;
          pu.unit = unit;
          supplier.purchasingUnits.push(pu);
        }
        pu.pid = supplier.pid;
        pu.internationalId = supplier.internationalId;
        if (this.gevisMappingList.length) {
          pu.gevisMapping = this.getGevisMappedValue(unit);
        }
      }
    }
  }

  private buildRows() {
    const map = new Map<string, PurchasingUnitRow>();
    for (const supplier of this.product.suppliers) {
      const supplierId = supplier.supplierId?.trim() ?? '';
      for (const pu of supplier.purchasingUnits ?? []) {
        const key = supplierId + '|' + (pu.unit ?? '');
        if (!map.has(key)) {
          map.set(key, {
            supplierId,
            supplierName: supplier.supplierName?.trim() || supplierId,
            supplierOrder: supplier.order?.toString(),
            unit: pu.unit,
            pid: pu.pid,
            internationalId: pu.internationalId,
            gevisMapping: pu.gevisMapping,
            contentQuantity: pu.contentQuantity,
            depth: pu.depth,
            length: pu.length,
            width: pu.width,
            weight: pu.weight,
            quantityMin: pu.quantityMin,
            quantityMax: pu.quantityMax,
            backing: []
          });
        }
        map.get(key).backing.push(pu);
      }
    }
    this.rows = [...map.values()];
  }

  private applyRowToBacking(row: PurchasingUnitRow) {
    for (const pu of row.backing) {
      pu.unit = row.unit;
      pu.pid = row.pid;
      pu.internationalId = row.internationalId;
      pu.gevisMapping = row.gevisMapping;
      pu.contentQuantity = row.contentQuantity;
      pu.depth = row.depth;
      pu.length = row.length;
      pu.width = row.width;
      pu.weight = row.weight;
      pu.quantityMin = row.quantityMin;
      pu.quantityMax = row.quantityMax;
    }
  }


  onBaseUnitChanged(e) {
    if (e.event == null) {
      return;
    }
    this.product.purchasingBaseUnit = e.value;
    this.emitChange();
    alert(
      this.translate.instant('Bitte nach Änderung der Grundeinheit die Mengen der Einkaufseinheiten prüfen'),
      this.translate.instant('Grundeinheit ändern')
    );
  }

  getBaseUnit(): string {
    return this.product.purchasingBaseUnit;
  }

  getUnitDisplay(unit: string): string {
    if (!unit) {
      return '';
    }
    const gevisMappedValue = this.getGevisMappedValue(unit);
    if (gevisMappedValue) {
      return gevisMappedValue;
    }
    const unitName = this.translate.instant(unit);
    return unitName && unitName !== unit ? `${unitName} (${unit})` : unit;
  }

  private emitChange() {
    this.onValueChanged.emit();
  }

  isVirtualCatalog(): boolean {
    return this.modelService?.catalogService?.catalog?.isVirtual;
  }

  readOnly(name: string, catalog: Catalog = this.modelService.catalogService.catalog): boolean {
    return this.isVirtualCatalog() || !this.userManagement.canEdit(name, catalog);
  }
}
